import { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import {
  BarChart3,
  CalendarDays,
  CheckCircle2,
  Download,
  FileSpreadsheet,
  FileText,
  Filter,
  LineChart,
  Mail,
  RefreshCw,
  Save,
  Search,
  Settings2,
  TrendingDown,
  TrendingUp,
  Upload,
  Users,
} from 'lucide-react';
import { useAuth } from '@/core/hooks';

type PeriodFilter = 'day' | 'week' | 'month' | 'quarter';
type DashboardKey = 'overview' | 'financial' | 'operational' | 'carers' | 'clients';
type ExportFormat = 'PDF' | 'CSV' | 'Excel';

interface ReportMetric {
  id: string;
  label: string;
  current: number;
  previous: number;
  forecast: number;
  unit: 'currency' | 'percent' | 'number' | 'hours';
  goodWhen: 'up' | 'down';
}

interface AdvancedReportSnapshot {
  generatedAt: string;
  source: string;
  window: {
    period: PeriodFilter;
    startDate: string;
    endDate: string;
    previousStartDate: string;
    previousEndDate: string;
  };
  summary: Record<string, number>;
  dashboards: Record<DashboardKey, ReportMetric[]>;
  drilldowns: {
    clients: Array<Record<string, string | number>>;
    carers: Array<Record<string, string | number>>;
    visits: Array<Record<string, string | number>>;
    audit: Array<Record<string, string | number>>;
    documentAccess: Array<Record<string, string | number>>;
  };
  scheduledReports: ScheduledReport[];
  savedFilters: SavedReportFilter[];
  deliveryLogs: ReportDeliveryLog[];
  dashboardPreferences: DashboardPreference[];
}

interface SavedReportFilter {
  id: string;
  name: string;
  dashboard: string;
  period: string;
  category: string;
  search: string;
  createdBy?: string;
}

interface ScheduledReport {
  id: string;
  name: string;
  dashboard: string;
  period: string;
  format: ExportFormat;
  cadence: 'daily' | 'weekly' | 'monthly' | 'quarterly';
  recipientEmail: string;
  enabled: boolean;
  nextRunAt?: string;
  lastRunAt?: string;
}

interface ReportDeliveryLog {
  id: string;
  reportName: string;
  recipientEmail: string;
  status: string;
  format: string;
  sentAt?: string;
  message?: string;
}

interface DashboardPreference {
  id: string;
  role: string;
  userId?: string;
  dashboardOrder: string[];
  widgets: string[];
}

const dashboardLabels: Record<DashboardKey, string> = {
  overview: 'Overview',
  financial: 'Financial',
  operational: 'Operational',
  carers: 'Carers',
  clients: 'Clients',
};

const periodLabels: Record<PeriodFilter, string> = {
  day: 'Day',
  week: 'Week',
  month: 'Month',
  quarter: 'Quarter',
};

const reportCatalog = [
  ['Management Pack', 'overview', 'Full management report with finance, visits, carers, compliance and forecast.'],
  ['Financial Performance', 'financial', 'Revenue, payroll cost, profit, invoices and cost forecast.'],
  ['Operations Control', 'operational', 'Visits, missed visits, medication alerts and compliance action pressure.'],
  ['Carer Workforce', 'carers', 'Utilisation, payroll, overtime, verified hours and capacity.'],
  ['Client Performance', 'clients', 'Client revenue, care hours, active clients, risk and profitability.'],
] as const;

function headers(token?: string | null): HeadersInit {
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function getJson<T>(url: string, token?: string | null): Promise<T> {
  const response = await fetch(url, { headers: headers(token) });
  if (!response.ok) throw new Error(`Could not load ${url}`);
  return response.json() as Promise<T>;
}

async function saveRecord<T>(collection: string, payload: Partial<T>, token?: string | null): Promise<T> {
  const response = await fetch(`/api/v1/${collection}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...headers(token),
    },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const errorPayload = await response.json().catch(() => ({})) as { error?: string };
    throw new Error(errorPayload.error || `Could not save ${collection}`);
  }
  const result = await response.json() as { data?: T };
  if (!result.data) throw new Error(`Empty ${collection} response`);
  return result.data;
}

export function ReportsPage() {
  const { token, user } = useAuth();
  const [period, setPeriod] = useState<PeriodFilter>('month');
  const [activeDashboard, setActiveDashboard] = useState<DashboardKey>('overview');
  const [snapshot, setSnapshot] = useState<AdvancedReportSnapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [drilldown, setDrilldown] = useState<'clients' | 'carers' | 'visits' | 'audit' | 'documentAccess'>('clients');
  const [filterName, setFilterName] = useState('Monthly management review');
  const [scheduleName, setScheduleName] = useState('Weekly management pack');
  const [scheduleEmail, setScheduleEmail] = useState('lowestoft@fuchsiahomecare.co.uk');
  const [scheduleCadence, setScheduleCadence] = useState<ScheduledReport['cadence']>('weekly');
  const [scheduleFormat, setScheduleFormat] = useState<ExportFormat>('PDF');
  const [roleView, setRoleView] = useState(user?.roles?.[0] || 'ADMIN');

  const loadReports = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getJson<AdvancedReportSnapshot>(`/api/reports/advanced?period=${period}`, token);
      setSnapshot(data);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not load reporting data');
    } finally {
      setLoading(false);
    }
  }, [period, token]);

  useEffect(() => {
    void loadReports();
  }, [loadReports]);

  const metrics = snapshot?.dashboards?.[activeDashboard] || [];
  const summary = snapshot?.summary || {};
  const filteredDrilldownRows = useMemo(() => {
    const rows = snapshot?.drilldowns?.[drilldown] || [];
    const normalizedSearch = search.trim().toLowerCase();
    if (!normalizedSearch) return rows;
    return rows.filter((row) => Object.values(row).join(' ').toLowerCase().includes(normalizedSearch));
  }, [drilldown, search, snapshot]);

  const aiSummary = useMemo(() => {
    const riskMetric = [...metrics].sort((a, b) => Math.abs((b.current || 0) - (b.previous || 0)) - Math.abs((a.current || 0) - (a.previous || 0)))[0];
    return [
      `I understood that you need a ${periodLabels[period].toLowerCase()} report for ${dashboardLabels[activeDashboard]}.`,
      `This report is calculated from the encrypted server database, not local demo data.`,
      `Revenue forecast is ${formatValue(Number(summary.revenueForecast || 0), 'currency')}, cost forecast is ${formatValue(Number(summary.costForecast || 0), 'currency')}, and capacity forecast is ${formatValue(Number(summary.capacityForecast || 0), 'hours')}.`,
      riskMetric ? `Main attention point: ${riskMetric.label} moved by ${formatDelta(riskMetric)} compared with the previous period.` : 'Main attention point: no major period movement detected.',
      'Recommended action: review drill-down rows first, export the PDF for manager record, then use CSV/Excel for deeper operational or finance work.',
    ].join('\n\n');
  }, [activeDashboard, metrics, period, summary]);

  const saveCurrentFilter = async () => {
    try {
      const saved = await saveRecord<SavedReportFilter>('savedReportFilters', {
        name: filterName,
        dashboard: activeDashboard,
        period,
        category: drilldown,
        search,
        createdBy: user?.email || 'admin',
      }, token);
      setSnapshot((current) => current ? { ...current, savedFilters: [saved, ...current.savedFilters] } : current);
      toast.success('Filter saved on the server.');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not save filter');
    }
  };

  const createScheduledReport = async () => {
    if (!scheduleEmail.includes('@')) {
      toast.error('Add a valid email address.');
      return;
    }
    try {
      const scheduled = await saveRecord<ScheduledReport>('scheduledReports', {
        name: scheduleName,
        dashboard: activeDashboard,
        period,
        format: scheduleFormat,
        cadence: scheduleCadence,
        recipientEmail: scheduleEmail,
        enabled: true,
        createdBy: user?.email || 'admin',
      }, token);
      setSnapshot((current) => current ? { ...current, scheduledReports: [scheduled, ...current.scheduledReports] } : current);
      toast.success('Scheduled report saved. Server scheduler will send it when due.');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not schedule report');
    }
  };

  const saveDashboardPreference = async () => {
    try {
      const preference = await saveRecord<DashboardPreference>('dashboardPreferences', {
        role: roleView,
        userId: user?.id,
        dashboardOrder: [activeDashboard, 'overview', 'financial', 'operational', 'carers', 'clients'],
        widgets: metrics.map((metric) => metric.id),
      }, token);
      setSnapshot((current) => current ? { ...current, dashboardPreferences: [preference, ...current.dashboardPreferences] } : current);
      toast.success('Dashboard preference saved for role.');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not save dashboard preference');
    }
  };

  const exportCurrent = (format: ExportFormat) => {
    const rows = filteredDrilldownRows;
    const name = `${dashboardLabels[activeDashboard].toLowerCase()}-${period}`;
    if (format === 'PDF') {
      downloadBlob(buildSimplePdf(`${dashboardLabels[activeDashboard]} Report`, buildReportLines(metrics, rows, aiSummary, snapshot)), `${name}.pdf`);
      toast.success('PDF report downloaded.');
      return;
    }
    if (format === 'CSV') {
      downloadBlob(new Blob([buildCsv(metrics, rows, aiSummary)], { type: 'text/csv;charset=utf-8' }), `${name}.csv`);
      toast.success('CSV report downloaded.');
      return;
    }
    downloadBlob(new Blob([buildExcelHtml(metrics, rows, aiSummary)], { type: 'application/vnd.ms-excel' }), `${name}.xls`);
    toast.success('Excel report downloaded.');
  };

  const importReportFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const text = await file.text();
    await saveRecord<ReportDeliveryLog>('reportDeliveryLogs', {
      reportName: `Imported ${file.name}`,
      recipientEmail: user?.email || 'admin',
      status: 'IMPORTED',
      format: file.name.endsWith('.csv') ? 'CSV' : file.name.endsWith('.xls') || file.name.endsWith('.xlsx') ? 'Excel' : 'PDF',
      message: `Imported ${text.split(/\r?\n/).filter(Boolean).length} rows for evidence/reconciliation.`,
      sentAt: new Date().toISOString(),
    }, token);
    toast.success('Import recorded in reporting logs.');
    event.target.value = '';
    void loadReports();
  };

  return (
    <div className="space-y-6 p-6">
      <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-5 xl:flex-row xl:items-start xl:justify-between">
          <div className="flex items-start gap-4">
            <div className="rounded-md bg-[#6c3a80]/10 p-3 text-[#6c3a80]">
              <BarChart3 className="h-7 w-7" />
            </div>
            <div>
              <p className="text-sm font-semibold uppercase tracking-wide text-[#6c3a80]">Advanced Reporting</p>
              <h1 className="mt-1 text-3xl font-bold text-slate-950">Reporting Command Centre</h1>
              <p className="mt-3 max-w-3xl text-sm leading-6 text-slate-600">
                Real database reports, scheduled email reporting, saved filters, role dashboards, drill-down, period comparisons, forecasts and PDF/CSV/Excel import/export.
              </p>
              <p className="mt-2 text-xs font-semibold text-emerald-700">
                Source: {snapshot?.source || 'loading'} | Generated: {snapshot?.generatedAt ? new Date(snapshot.generatedAt).toLocaleString('en-GB') : 'not yet'}
              </p>
            </div>
          </div>
          <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-5">
            <select value={period} onChange={(event) => setPeriod(event.target.value as PeriodFilter)} className="h-10 rounded-md border border-slate-300 px-3 text-sm">
              <option value="day">Day</option>
              <option value="week">Week</option>
              <option value="month">Month</option>
              <option value="quarter">Quarter</option>
            </select>
            <button type="button" onClick={() => void loadReports()} className="inline-flex h-10 items-center justify-center gap-2 rounded-md border border-slate-300 px-4 text-sm font-semibold text-slate-700 hover:bg-slate-50">
              <RefreshCw className="h-4 w-4" />
              Refresh
            </button>
            <button type="button" onClick={() => exportCurrent('PDF')} className="inline-flex h-10 items-center justify-center gap-2 rounded-md bg-[#6c3a80] px-4 text-sm font-semibold text-white hover:bg-[#5e3474]">
              <Download className="h-4 w-4" />
              PDF
            </button>
            <button type="button" onClick={() => exportCurrent('CSV')} className="inline-flex h-10 items-center justify-center gap-2 rounded-md border border-slate-300 px-4 text-sm font-semibold text-slate-700 hover:bg-slate-50">
              <FileSpreadsheet className="h-4 w-4" />
              CSV
            </button>
            <button type="button" onClick={() => exportCurrent('Excel')} className="inline-flex h-10 items-center justify-center gap-2 rounded-md border border-slate-300 px-4 text-sm font-semibold text-slate-700 hover:bg-slate-50">
              <FileSpreadsheet className="h-4 w-4" />
              Excel
            </button>
          </div>
        </div>
      </section>

      <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
        <MetricCard label="Revenue" value={formatValue(Number(summary.revenue || 0), 'currency')} icon={<TrendingUp className="h-5 w-5" />} tone="success" />
        <MetricCard label="Costs" value={formatValue(Number(summary.costs || 0), 'currency')} icon={<TrendingDown className="h-5 w-5" />} tone="warning" />
        <MetricCard label="Profit" value={formatValue(Number(summary.grossProfit || 0), 'currency')} icon={<LineChart className="h-5 w-5" />} tone="success" />
        <MetricCard label="Completed visits" value={String(summary.completedVisits || 0)} icon={<CheckCircle2 className="h-5 w-5" />} />
        <MetricCard label="Capacity forecast" value={formatValue(Number(summary.capacityForecast || 0), 'hours')} icon={<Users className="h-5 w-5" />} />
      </section>

      <div className="border-b border-slate-200">
        <nav className="-mb-px flex flex-wrap gap-5">
          {(Object.keys(dashboardLabels) as DashboardKey[]).map((key) => (
            <button
              key={key}
              type="button"
              onClick={() => setActiveDashboard(key)}
              className={`border-b-2 px-1 py-4 text-sm font-semibold transition ${activeDashboard === key ? 'border-[#6c3a80] text-[#6c3a80]' : 'border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-700'}`}
            >
              {dashboardLabels[key]}
            </button>
          ))}
        </nav>
      </div>

      {loading ? (
        <div className="rounded-lg border border-slate-200 bg-white p-8 text-center text-slate-600">Loading reporting data from encrypted server database...</div>
      ) : null}

      {!loading ? (
        <>
          <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            {metrics.map((metric) => <DashboardMetricCard key={metric.id} metric={metric} />)}
          </section>

          <section className="grid gap-6 xl:grid-cols-[1fr_0.8fr]">
            <article className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                <div>
                  <h2 className="text-xl font-bold text-slate-950">Drill-down</h2>
                  <p className="mt-1 text-sm text-slate-600">Open the detail behind every report by client, carer, visit, audit or document access.</p>
                </div>
                <div className="grid gap-2 sm:grid-cols-[180px_1fr]">
                  <select value={drilldown} onChange={(event) => setDrilldown(event.target.value as typeof drilldown)} className="h-10 rounded-md border border-slate-300 px-3 text-sm">
                    <option value="clients">Clients</option>
                    <option value="carers">Carers</option>
                    <option value="visits">Visits</option>
                    <option value="audit">Audit</option>
                    <option value="documentAccess">Document access</option>
                  </select>
                  <label className="relative block">
                    <Search className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-slate-400" />
                    <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search drill-down rows" className="h-10 w-full rounded-md border border-slate-300 pl-9 pr-3 text-sm" />
                  </label>
                </div>
              </div>
              <DataTable rows={filteredDrilldownRows} />
            </article>

            <article className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
              <div className="flex items-center gap-3">
                <LineChart className="h-5 w-5 text-[#6c3a80]" />
                <h2 className="text-xl font-bold text-slate-950">AI report summary</h2>
              </div>
              <div className="mt-4 min-h-56 rounded-md bg-slate-50 p-4 text-sm leading-6 text-slate-700 whitespace-pre-line">{aiSummary}</div>
            </article>
          </section>

          <section className="grid gap-5 xl:grid-cols-3">
            <Panel title="Saved Filters" icon={<Filter className="h-5 w-5" />}>
              <input value={filterName} onChange={(event) => setFilterName(event.target.value)} className="h-10 w-full rounded-md border border-slate-300 px-3 text-sm" />
              <button type="button" onClick={() => void saveCurrentFilter()} className="mt-3 inline-flex h-10 items-center gap-2 rounded-md bg-[#6c3a80] px-4 text-sm font-semibold text-white hover:bg-[#5e3474]">
                <Save className="h-4 w-4" />
                Save current filter
              </button>
              <List items={(snapshot?.savedFilters || []).map((item) => `${item.name} | ${item.dashboard} | ${item.period}`)} />
            </Panel>

            <Panel title="Scheduled Email Reports" icon={<Mail className="h-5 w-5" />}>
              <input value={scheduleName} onChange={(event) => setScheduleName(event.target.value)} className="h-10 w-full rounded-md border border-slate-300 px-3 text-sm" />
              <input value={scheduleEmail} onChange={(event) => setScheduleEmail(event.target.value)} className="mt-2 h-10 w-full rounded-md border border-slate-300 px-3 text-sm" />
              <div className="mt-2 grid grid-cols-2 gap-2">
                <select value={scheduleCadence} onChange={(event) => setScheduleCadence(event.target.value as ScheduledReport['cadence'])} className="h-10 rounded-md border border-slate-300 px-3 text-sm">
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                </select>
                <select value={scheduleFormat} onChange={(event) => setScheduleFormat(event.target.value as ExportFormat)} className="h-10 rounded-md border border-slate-300 px-3 text-sm">
                  <option>PDF</option>
                  <option>CSV</option>
                  <option>Excel</option>
                </select>
              </div>
              <button type="button" onClick={() => void createScheduledReport()} className="mt-3 inline-flex h-10 items-center gap-2 rounded-md bg-[#6c3a80] px-4 text-sm font-semibold text-white hover:bg-[#5e3474]">
                <CalendarDays className="h-4 w-4" />
                Schedule report
              </button>
              <List items={(snapshot?.scheduledReports || []).map((item) => `${item.name} | ${item.cadence} | ${item.recipientEmail}`)} />
            </Panel>

            <Panel title="Role Dashboard & Import" icon={<Settings2 className="h-5 w-5" />}>
              <select value={roleView} onChange={(event) => setRoleView(event.target.value)} className="h-10 w-full rounded-md border border-slate-300 px-3 text-sm">
                <option>ADMIN</option>
                <option>MANAGER</option>
                <option>COORDINATOR</option>
                <option>CARER</option>
                <option>FAMILY</option>
              </select>
              <button type="button" onClick={() => void saveDashboardPreference()} className="mt-3 inline-flex h-10 items-center gap-2 rounded-md bg-[#6c3a80] px-4 text-sm font-semibold text-white hover:bg-[#5e3474]">
                <Save className="h-4 w-4" />
                Save role dashboard
              </button>
              <label className="mt-3 inline-flex h-10 cursor-pointer items-center gap-2 rounded-md border border-slate-300 px-4 text-sm font-semibold text-slate-700 hover:bg-slate-50">
                <Upload className="h-4 w-4" />
                Import CSV/Excel/PDF
                <input type="file" accept=".csv,.xls,.xlsx,.pdf" onChange={(event) => void importReportFile(event)} className="hidden" />
              </label>
              <List items={(snapshot?.deliveryLogs || []).slice(0, 5).map((item) => `${item.reportName} | ${item.status} | ${item.message || ''}`)} />
            </Panel>
          </section>

          <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
            <h2 className="text-xl font-bold text-slate-950">Report catalogue</h2>
            <div className="mt-4 grid gap-4 lg:grid-cols-2">
              {reportCatalog.map(([name, dashboard, description]) => (
                <article key={name} className="rounded-md border border-slate-200 p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h3 className="font-bold text-slate-950">{name}</h3>
                      <p className="mt-1 text-sm leading-6 text-slate-600">{description}</p>
                    </div>
                    <span className="rounded-full bg-purple-100 px-2 py-1 text-xs font-semibold text-purple-800">{dashboard}</span>
                  </div>
                </article>
              ))}
            </div>
          </section>
        </>
      ) : null}
    </div>
  );
}

function DataTable({ rows }: { rows: Array<Record<string, string | number>> }) {
  const columns = Object.keys(rows[0] || { Empty: 'No rows' });
  return (
    <div className="mt-5 overflow-x-auto">
      <table className="min-w-full divide-y divide-slate-200 text-sm">
        <thead className="bg-slate-50 text-left text-xs font-semibold uppercase text-slate-500">
          <tr>{columns.map((column) => <th key={column} className="px-4 py-3">{column}</th>)}</tr>
        </thead>
        <tbody className="divide-y divide-slate-200 bg-white">
          {rows.length ? rows.map((row, index) => (
            <tr key={String(row.id || index)}>
              {columns.map((column) => <td key={column} className="px-4 py-3 text-slate-700 first:font-semibold first:text-slate-950">{String(row[column] ?? '')}</td>)}
            </tr>
          )) : (
            <tr><td className="px-4 py-4 text-slate-600" colSpan={columns.length}>No rows for this filter.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function DashboardMetricCard({ metric }: { metric: ReportMetric }) {
  const delta = Number(metric.current || 0) - Number(metric.previous || 0);
  const isGood = metric.goodWhen === 'up' ? delta >= 0 : delta <= 0;
  return (
    <article className={`rounded-lg border p-4 shadow-sm ${isGood ? 'border-emerald-200 bg-emerald-50' : 'border-amber-200 bg-amber-50'}`}>
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs font-semibold uppercase text-slate-500">{metric.label}</p>
        {isGood ? <TrendingUp className="h-5 w-5 text-emerald-600" /> : <TrendingDown className="h-5 w-5 text-amber-600" />}
      </div>
      <p className="mt-2 text-3xl font-bold text-slate-950">{formatValue(metric.current, metric.unit)}</p>
      <p className={`mt-1 text-sm font-semibold ${isGood ? 'text-emerald-700' : 'text-amber-800'}`}>{formatDelta(metric)} vs previous period</p>
      <p className="mt-2 text-sm text-slate-700">Forecast: {formatValue(metric.forecast, metric.unit)}</p>
    </article>
  );
}

function MetricCard({ label, value, icon, tone = 'default' }: { label: string; value: string; icon: React.ReactNode; tone?: 'default' | 'success' | 'warning' }) {
  const styles = {
    default: 'border-slate-200 bg-white',
    success: 'border-emerald-200 bg-emerald-50',
    warning: 'border-amber-200 bg-amber-50',
  };
  return (
    <article className={`rounded-lg border p-4 shadow-sm ${styles[tone]}`}>
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs font-semibold uppercase text-slate-500">{label}</p>
        <div className="text-[#6c3a80]">{icon}</div>
      </div>
      <p className="mt-2 text-3xl font-bold text-slate-950">{value}</p>
    </article>
  );
}

function Panel({ title, icon, children }: { title: string; icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <article className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
      <div className="mb-4 flex items-center gap-3 text-[#6c3a80]">
        {icon}
        <h2 className="text-lg font-bold text-slate-950">{title}</h2>
      </div>
      {children}
    </article>
  );
}

function List({ items }: { items: string[] }) {
  return (
    <div className="mt-4 space-y-2">
      {items.length ? items.map((item, index) => <div key={`${item}-${index}`} className="rounded-md bg-slate-50 p-3 text-xs leading-5 text-slate-700">{item}</div>) : <div className="rounded-md bg-slate-50 p-3 text-xs text-slate-500">No records yet.</div>}
    </div>
  );
}

function formatDelta(metric: ReportMetric) {
  const delta = Number(metric.current || 0) - Number(metric.previous || 0);
  const sign = delta >= 0 ? '+' : '';
  return `${sign}${formatValue(delta, metric.unit)}`;
}

function formatValue(value: number, unit: ReportMetric['unit']) {
  if (unit === 'currency') return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 }).format(value);
  if (unit === 'percent') return `${Math.round(value * 10) / 10}%`;
  if (unit === 'hours') return `${Math.round(value * 10) / 10}h`;
  return new Intl.NumberFormat('en-GB', { maximumFractionDigits: 1 }).format(value);
}

function buildCsv(metrics: ReportMetric[], rows: Array<Record<string, string | number>>, aiSummary: string) {
  const metricLines = [
    ['Metric', 'Current', 'Previous', 'Delta', 'Forecast'],
    ...metrics.map((metric) => [metric.label, formatValue(metric.current, metric.unit), formatValue(metric.previous, metric.unit), formatDelta(metric), formatValue(metric.forecast, metric.unit)]),
    [],
    ['AI Summary', aiSummary],
    [],
  ];
  const rowColumns = Object.keys(rows[0] || {});
  const rowLines = rows.length ? [rowColumns, ...rows.map((row) => rowColumns.map((column) => row[column] ?? ''))] : [['No drill-down rows']];
  return [...metricLines, ...rowLines].map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(',')).join('\n');
}

function buildExcelHtml(metrics: ReportMetric[], rows: Array<Record<string, string | number>>, aiSummary: string) {
  const metricRows = metrics.map((metric) => `<tr><td>${metric.label}</td><td>${formatValue(metric.current, metric.unit)}</td><td>${formatValue(metric.previous, metric.unit)}</td><td>${formatDelta(metric)}</td><td>${formatValue(metric.forecast, metric.unit)}</td></tr>`).join('');
  const rowColumns = Object.keys(rows[0] || {});
  const detailRows = rows.map((row) => `<tr>${rowColumns.map((column) => `<td>${String(row[column] ?? '')}</td>`).join('')}</tr>`).join('');
  return `<html><body><h1>Fuchsia Homecare Report</h1><p>${aiSummary.replace(/\n/g, '<br>')}</p><table border="1"><tr><th>Metric</th><th>Current</th><th>Previous</th><th>Delta</th><th>Forecast</th></tr>${metricRows}</table><br><table border="1"><tr>${rowColumns.map((column) => `<th>${column}</th>`).join('')}</tr>${detailRows}</table></body></html>`;
}

function buildReportLines(metrics: ReportMetric[], rows: Array<Record<string, string | number>>, aiSummary: string, snapshot: AdvancedReportSnapshot | null) {
  return [
    `Generated: ${new Date().toLocaleString('en-GB')}`,
    `Source: ${snapshot?.source || 'server database'}`,
    `Period: ${snapshot?.window.startDate || ''} to ${snapshot?.window.endDate || ''}`,
    '',
    'Metrics',
    ...metrics.map((metric) => `${metric.label}: current ${formatValue(metric.current, metric.unit)}, previous ${formatValue(metric.previous, metric.unit)}, delta ${formatDelta(metric)}, forecast ${formatValue(metric.forecast, metric.unit)}`),
    '',
    'AI Summary',
    aiSummary,
    '',
    'Drill-down sample',
    ...rows.slice(0, 20).map((row) => Object.entries(row).map(([key, value]) => `${key}: ${value}`).join(' | ')),
  ];
}

function sanitizePdfText(value: string): string {
  return value.normalize('NFKD').replace(/[\u0300-\u036f]/g, '').replace(/[^\x20-\x7E\n\r\t]/g, ' ').replace(/[()\\]/g, (match) => `\\${match}`).trim();
}

function buildSimplePdf(title: string, rawLines: string[]): Blob {
  const lines = [title, 'FUCHSIA HOME CARE CATALIN', 'Yarmouth Business Park, Suffolk Rd, Great Yarmouth, NR31 0LN', '', ...rawLines]
    .flatMap((line) => String(line).split(/\n/))
    .flatMap((line) => line.match(/.{1,92}(\s|$)/g) || [line])
    .map(sanitizePdfText);
  const content = ['BT', '/F1 10 Tf', '48 790 Td', '14 TL', ...lines.slice(0, 56).map((line, index) => index === 0 ? `/F1 16 Tf (${line}) Tj /F1 10 Tf T*` : `(${line}) Tj T*`), 'ET'].join('\n');
  const objects = [
    '1 0 obj << /Type /Catalog /Pages 2 0 R >> endobj',
    '2 0 obj << /Type /Pages /Kids [3 0 R] /Count 1 >> endobj',
    '3 0 obj << /Type /Page /Parent 2 0 R /MediaBox [0 0 595 842] /Resources << /Font << /F1 4 0 R >> >> /Contents 5 0 R >> endobj',
    '4 0 obj << /Type /Font /Subtype /Type1 /BaseFont /Helvetica >> endobj',
    `5 0 obj << /Length ${content.length} >> stream\n${content}\nendstream endobj`,
  ];
  let pdf = '%PDF-1.4\n';
  const offsets = [0];
  objects.forEach((object) => {
    offsets.push(pdf.length);
    pdf += `${object}\n`;
  });
  const xrefOffset = pdf.length;
  pdf += `xref\n0 ${objects.length + 1}\n0000000000 65535 f \n`;
  offsets.slice(1).forEach((offset) => { pdf += `${String(offset).padStart(10, '0')} 00000 n \n`; });
  pdf += `trailer << /Size ${objects.length + 1} /Root 1 0 R >>\nstartxref\n${xrefOffset}\n%%EOF`;
  return new Blob([pdf], { type: 'application/pdf' });
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}
